<template>
  <div class="flex flex-col w-full gap-12">
    <!-- Profile Header Section -->
    <div class="flex gap-6 w-full flex-col lg:flex-row">
      <!-- Main Profile Card -->
      <div class="flex flex-col flex-[8] rounded-lg overflow-hidden bg-gray-900">
        <!-- Banner Skeleton -->
        <div class="h-[300px] w-full bg-gray-800 animate-pulse" />

        <!-- Profile Info Skeleton -->
        <div class="flex px-6 py-[28px] gap-4 items-center">
          <!-- Avatar Skeleton -->
          <div class="w-[72px] h-[72px] rounded-full bg-gray-800 animate-pulse" />

          <!-- Name and Role Skeleton -->
          <div class="flex flex-col gap-2 justify-center flex-1">
            <div class="flex gap-2 items-center">
              <div class="h-9 w-48 bg-gray-800 rounded animate-pulse" />
              <div class="h-8 w-12 bg-gray-800 rounded animate-pulse" />
            </div>
            <div class="h-5 w-24 bg-gray-800 rounded animate-pulse" />
          </div>

          <!-- Social Links Skeleton -->
          <div class="flex gap-2">
            <div class="h-10 w-16 bg-gray-800 rounded-lg animate-pulse" />
            <div class="h-10 w-16 bg-gray-800 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>

      <!-- Badges Section -->
      <div class="flex-[4] rounded-lg overflow-hidden bg-gray-900 flex flex-col">
        <!-- Badges Header -->
        <div class="p-6 border-b-2 border-gray-950">
          <div class="h-7 w-32 bg-gray-800 rounded animate-pulse" />
        </div>

        <!-- Featured Badge -->
        <div class="w-full p-6 flex gap-4 relative border-b-2 border-gray-950">
          <div class="w-full absolute top-0 left-0 h-full z-20 topography-overlay" />
          <div class="w-full absolute top-0 left-0 h-full first-background z-10" />

          <div class="h-24 w-24 min-w-[96px] bg-gray-800 rounded animate-pulse z-50" />
          <div class="flex flex-col gap-2 justify-center z-50">
            <div class="h-6 w-32 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-48 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-40 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>

        <!-- Additional Badges -->
        <div class="flex p-6 flex-wrap gap-2">
          <div v-for="i in 6" :key="i" class="h-16 w-16 bg-gray-800 rounded animate-pulse" />
        </div>
      </div>
    </div>

    <!-- Navigation Skeleton -->
    <div class="w-full border-b border-gray-800 pb-4">
      <div class="flex gap-9">
        <div class="h-6 w-20 bg-gray-800 rounded animate-pulse" />
        <div class="h-6 w-24 bg-gray-800 rounded animate-pulse" />
        <div class="h-6 w-20 bg-gray-800 rounded animate-pulse" />
        <div class="h-6 w-32 bg-gray-800 rounded animate-pulse" />
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="flex flex-col gap-6 w-full">
      <div class="h-8 w-24 bg-gray-800 rounded animate-pulse" />

      <!-- ELO Charts and Stats Row -->
      <div class="flex gap-6 w-full flex-col lg:flex-row">
        <!-- Quickfire ELO Skeleton -->
        <div class="flex flex-1 py-2 items-center justify-center rounded-lg relative bg-gray-900">
          <div class="w-[208px] h-[208px] rounded-full bg-gray-800 animate-pulse" />
          <div class="absolute w-full h-full top-0 left-0 flex flex-col items-center justify-center mt-3">
            <div class="w-16 h-6 bg-gray-700 rounded-full mb-3 animate-pulse" />
            <div class="w-24 h-4 bg-gray-700 rounded mb-2 animate-pulse" />
            <div class="w-16 h-10 bg-gray-700 rounded animate-pulse" />
          </div>
        </div>

        <!-- Prefire ELO Skeleton -->
        <div class="flex flex-1 py-2 items-center justify-center rounded-lg relative bg-gray-900">
          <div class="w-[208px] h-[208px] rounded-full bg-gray-800 animate-pulse" />
          <div class="absolute w-full h-full top-0 left-0 flex flex-col items-center justify-center mt-3">
            <div class="w-16 h-6 bg-gray-700 rounded-full mb-3 animate-pulse" />
            <div class="w-20 h-4 bg-gray-700 rounded mb-2 animate-pulse" />
            <div class="w-16 h-10 bg-gray-700 rounded animate-pulse" />
          </div>
        </div>

        <!-- Stats Panel Skeleton -->
        <div class="flex flex-1 bg-gray-900 rounded-lg flex-col divide-y-2 divide-gray-950">
          <div class="px-6 py-4 flex items-center gap-2">
            <div class="h-5 w-20 bg-gray-800 rounded animate-pulse flex-1" />
            <div class="h-6 w-12 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="px-6 py-4 flex">
            <div class="h-5 w-24 bg-gray-800 rounded animate-pulse flex-1" />
            <div class="h-6 w-16 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="px-6 py-4 flex">
            <div class="h-5 w-32 bg-gray-800 rounded animate-pulse flex-1" />
            <div class="h-6 w-12 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="px-6 py-4 flex">
            <div class="h-5 w-28 bg-gray-800 rounded animate-pulse flex-1" />
            <div class="h-6 w-14 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
      </div>

      <!-- ELO Over Time Chart Skeleton -->
      <div class="bg-gray-900 rounded-lg p-6">
        <div class="h-[300px] w-full bg-gray-800 rounded animate-pulse" />
      </div>
    </div>

    <!-- Match History Section -->
    <div class="flex flex-col gap-6 w-full">
      <div class="flex w-full gap-4 items-center">
        <div class="h-8 w-32 bg-gray-800 rounded animate-pulse flex-1" />
        <div class="h-8 w-24 bg-gray-800 rounded animate-pulse" />
      </div>

      <!-- Match History Items -->
      <div class="flex flex-col gap-2">
        <div v-for="i in 5" :key="i" class="bg-gray-900 rounded-lg p-4 flex gap-4 items-center">
          <div class="w-16 h-16 bg-gray-800 rounded animate-pulse" />
          <div class="flex-1 flex flex-col gap-2">
            <div class="h-5 w-32 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-48 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="flex gap-2">
            <div class="h-8 w-16 bg-gray-800 rounded animate-pulse" />
            <div class="h-8 w-20 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// No props or logic needed - this is a pure skeleton component
</script>

<style scoped>
.topography-overlay {
  background-image: url("../../assets/topography-two.svg");
  background-repeat: repeat;
  background-size: 500px 500px;
  opacity: 0.1;
}

.first-background {
  background: linear-gradient(
    180deg,
    rgba(78, 84, 200, 0.1) 0%,
    rgba(38, 41, 98, 0.1) 100%
  );
}
</style>
